import React, { useCallback, useMemo } from 'react';
import { Box, Grid } from '@mui/material';
import {
    Autocomplete,
    Button,
    CheckboxGroup,
    ControlledFormInput,
    EntityAutocomplete,
    FormWrapper,
    RadioGroupField,
    SelectField,
    Slider,
    TextField,
    ToggleButtonGroup,
} from 'c-components';
import type { UseFormProps } from 'react-hook-form';
import {
    FormatAlignCenter,
    FormatAlignLeft,
    FormatAlignRight,
    VolumeUp,
} from '@mui/icons-material';

const Section = ({ items }: { items: React.ReactNode[] }) => (
    <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
        <Grid container spacing={2}>
            {items.map((i, ii) => (
                // eslint-disable-next-line react/no-array-index-key
                <Grid item key={ii} xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
                    {i}
                </Grid>
            ))}
        </Grid>
    </Grid>
);

const Forms = () => {
    const onSubmit = useCallback(data => {
        // don't care
    }, []);
    const formOptions = useMemo<UseFormProps<any>>(
        () => ({
            defaultValues: {
                sliderSmall: 25,
                sliderMedium: 25,
                sliderSmallSec: 50,
                sliderMediumSec: 50,
                sliderMediumIcon: 75,
                sliderMediumIconSec: 75,
                sliderMediumMarks: 4,
                sliderMediumRange: [20, 50],
            },
        }),
        [],
    );

    return (
        <Box>
            <FormWrapper onSubmit={onSubmit} formOptions={formOptions}>
                <Grid container spacing={4}>
                    <Section
                        items={[
                            <Button key="contained" variant="contained">
                                Button
                            </Button>,
                            <Button key="outlined" variant="outlined">
                                Button
                            </Button>,
                            <Button key="text" variant="text">
                                Button
                            </Button>,
                            <ControlledFormInput
                                key="toggle"
                                render={({ field }) => (
                                    <ToggleButtonGroup
                                        color="primary"
                                        exclusive
                                        options={[
                                            { label: 'Option 1', value: 1 },
                                            { label: 'Option 2', value: 2 },
                                            { label: 'Option 3', value: 3 },
                                        ]}
                                        {...field}
                                    />
                                )}
                                name="toggle"
                            />,
                            <ControlledFormInput
                                key="toggleSec"
                                render={({ field }) => (
                                    <ToggleButtonGroup
                                        color="secondary"
                                        exclusive
                                        options={[
                                            { label: <FormatAlignLeft />, value: 1 },
                                            { label: <FormatAlignCenter />, value: 2 },
                                            { label: <FormatAlignRight />, value: 3 },
                                        ]}
                                        {...field}
                                    />
                                )}
                                name="toggleSec"
                            />,
                        ]}
                    />
                    <Section
                        items={[
                            <ControlledFormInput
                                key="textFilled"
                                render={({ field }) => (
                                    <TextField label="TextField" variant="filled" {...field} />
                                )}
                                name="textFilled"
                            />,
                            <ControlledFormInput
                                key="textOutlined"
                                render={({ field }) => (
                                    <TextField label="TextField" variant="outlined" {...field} />
                                )}
                                name="textOutlined"
                            />,
                            <ControlledFormInput
                                key="textStandard"
                                render={({ field }) => (
                                    <TextField label="TextField" variant="standard" {...field} />
                                )}
                                name="textStandard"
                            />,
                        ]}
                    />
                    <Section
                        items={[
                            <ControlledFormInput
                                key="checkboxGroup"
                                render={({ field }) => (
                                    <CheckboxGroup
                                        label="Checkbox Group"
                                        multiple
                                        options={[
                                            { label: 'Option 1', value: 1 },
                                            { label: 'Option 2', value: 2 },
                                            { label: 'Option 3', value: 3 },
                                        ]}
                                        {...field}
                                    />
                                )}
                                name="checkboxGroup"
                            />,
                        ]}
                    />
                    <Section
                        items={[
                            <ControlledFormInput
                                key="radioGroup"
                                render={({ field }) => (
                                    <RadioGroupField
                                        label="Radio Group"
                                        options={[
                                            { label: 'Option 1', value: 1 },
                                            { label: 'Option 2', value: 2 },
                                            { label: 'Option 3', value: 3 },
                                        ]}
                                        {...field}
                                    />
                                )}
                                name="radioGroup"
                            />,
                        ]}
                    />
                    <Section
                        items={[
                            <ControlledFormInput
                                key="selectSingle"
                                render={({ field }) => (
                                    <SelectField
                                        label="Single Select"
                                        options={[
                                            { label: 'Option 1', value: '1' },
                                            { label: 'Option 2', value: '2' },
                                            { label: 'Option 3', value: '3' },
                                        ]}
                                        {...field}
                                    />
                                )}
                                name="selectSingle"
                            />,
                            <ControlledFormInput
                                key="selectMulti"
                                render={({ field }) => (
                                    <SelectField
                                        label="Multi Select"
                                        multiple
                                        options={[
                                            { label: 'Option 1', value: '1' },
                                            { label: 'Option 2', value: '2' },
                                            { label: 'Option 3', value: '3' },
                                        ]}
                                        {...field}
                                    />
                                )}
                                name="selectMulti"
                            />,
                            <ControlledFormInput
                                key="autocompleteMulti"
                                render={({ field }) => (
                                    <Autocomplete
                                        textFieldProps={{
                                            label: 'Autocomplete Select',
                                        }}
                                        multiple
                                        options={[
                                            { label: 'Option 1', value: '1' },
                                            { label: 'Option 2', value: '2' },
                                            { label: 'Option 3', value: '3' },
                                        ]}
                                        {...field}
                                    />
                                )}
                                name="autocompleteMulti"
                            />,
                            <ControlledFormInput
                                key="entityDropdownSingle"
                                render={({ field }) => (
                                    <EntityAutocomplete
                                        entityName="User"
                                        textFieldProps={{
                                            label: 'User Search',
                                        }}
                                        searchColumns="name"
                                        labelColumn="name"
                                        {...field}
                                    />
                                )}
                                name="entityDropdownSingle"
                            />,
                            <ControlledFormInput
                                key="entityDropdownMulti"
                                render={({ field }) => (
                                    <EntityAutocomplete
                                        entityName="User"
                                        textFieldProps={{
                                            label: 'User Search Multi',
                                        }}
                                        searchColumns="name"
                                        labelColumn="name"
                                        multiple
                                        {...field}
                                    />
                                )}
                                name="entityDropdownMulti"
                            />,
                        ]}
                    />
                    <Section
                        items={[
                            <ControlledFormInput
                                key="sliderSmall"
                                render={({ field }) => (
                                    <Slider label="Small Slider" size="small" {...field} />
                                )}
                                name="sliderSmall"
                            />,
                            <ControlledFormInput
                                key="sliderMedium"
                                render={({ field }) => (
                                    <Slider label="Normal Slider" size="medium" {...field} />
                                )}
                                name="sliderMedium"
                            />,
                            <ControlledFormInput
                                key="sliderSmallSec"
                                render={({ field }) => (
                                    <Slider
                                        label="Small Slider"
                                        size="small"
                                        color="secondary"
                                        {...field}
                                    />
                                )}
                                name="sliderSmallSec"
                            />,
                            <ControlledFormInput
                                key="sliderMediumSec"
                                render={({ field }) => (
                                    <Slider
                                        label="Normal Slider"
                                        size="medium"
                                        color="secondary"
                                        {...field}
                                    />
                                )}
                                name="sliderMediumSec"
                            />,
                            <ControlledFormInput
                                key="sliderMediumIcon"
                                render={({ field }) => (
                                    <Slider label="Icon Slider" icon={<VolumeUp />} {...field} />
                                )}
                                name="sliderMediumIcon"
                            />,
                            <ControlledFormInput
                                key="sliderMediumIconSec"
                                render={({ field }) => (
                                    <Slider
                                        label="Icon Slider"
                                        color="secondary"
                                        icon={<VolumeUp />}
                                        {...field}
                                    />
                                )}
                                name="sliderMediumIconSec"
                            />,
                            <ControlledFormInput
                                key="sliderMediumMarks"
                                render={({ field }) => (
                                    <Slider
                                        label="Marks Slider"
                                        max={10}
                                        marks={[
                                            {
                                                value: 0,
                                                label: '0',
                                            },
                                            {
                                                value: 2,
                                                label: '2',
                                            },
                                            {
                                                value: 6,
                                                label: '6',
                                            },
                                            {
                                                value: 10,
                                                label: '10',
                                            },
                                        ]}
                                        {...field}
                                    />
                                )}
                                name="sliderMediumMarks"
                            />,
                            <ControlledFormInput
                                key="sliderMediumRange"
                                render={({ field }) => (
                                    <Slider label="Range Slider" disableSwap {...field} />
                                )}
                                name="sliderMediumRange"
                            />,
                        ]}
                    />
                </Grid>
            </FormWrapper>
        </Box>
    );
};

export default Forms;
